import { splitEvery } from 'ramda';

import { updateVenueInventories } from 'apis/inventory-management-service/api';
import { bulkUpdateItemConfigurations, bulkUpdatePickingAreas } from 'apis/picker-api/api';
import {
  bulkChangeCategory,
  bulkUpdateItems,
  patchItemBindingsOrder,
} from 'apis/restaurant-api/merchant-admin/menu2/api';
import { BulkChangeCategoryPayload } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { bulkUpdateItemPricing } from 'apis/restaurant-api/merchant-admin/merchant/api';
import { isDefined, getErrorContent } from 'common/utils';

import { PayloadsTuple } from './utils';

const UPDATE_ITEMS_BATCH_SIZE_LIMIT = 200;
const PICKING_AREA_PAYLOAD_ITEMS_LIMIT = 1000;
const PICKING_AREA_PAYLOAD_AREAS_LIMIT = 100;

const concatErrors = (currentErrors: (string | undefined)[] | undefined, newError: string | undefined) =>
  [...(currentErrors || []), newError].filter(isDefined);

const uniquifyOrderByGroup = <
  U extends { order: number | null } & { [K in keyof U]: K extends 'order' ? number | null : string },
  T extends keyof U,
>(
  groupIdentifier: T,
  payload: U[],
) =>
  payload
    .sort((a, b) => {
      const nameCompare = a[groupIdentifier].localeCompare(b[groupIdentifier]);
      if (nameCompare !== 0) {
        return nameCompare;
      }
      if (a.order === null) {
        return 1;
      }
      if (b.order === null) {
        return -1;
      }
      return a.order - b.order;
    })
    .reduce<U[]>((acc, curr, index) => {
      const prev = acc[index - 1];
      if (
        !prev ||
        curr[groupIdentifier] !== prev[groupIdentifier] ||
        prev.order === null ||
        Number(curr.order) > prev.order
      ) {
        return [...acc, curr];
      }
      return [...acc, { ...curr, order: prev.order + 1 }];
    }, [])
    .filter((value): value is U & { order: number } => value.order !== null);

const combineCategoryChanges = (categoriesPayloads: BulkChangeCategoryPayload[]) => {
  const indexedPayloadsByDestId = categoriesPayloads.reduce<Record<string, BulkChangeCategoryPayload>>((acc, curr) => {
    const destId = curr.destination_category_id?.$oid || 'null';
    return acc[destId]
      ? { ...acc, [destId]: { ...curr, item_ids: [...acc[destId].item_ids, ...curr.item_ids] } }
      : { ...acc, [destId]: curr };
  }, {});
  return Object.values(indexedPayloadsByDestId);
};

const submitHandler = async (
  merchantId: string,
  venueId: string,
  menuId: string,
  payloads: PayloadsTuple,
  setSubmitPercentage: (percentage: number) => void,
) => {
  const [
    itemUpdatePayloads,
    itemInventoryPayloads,
    itemConfigsPayloads,
    itemCommissionPayloads,
    pickingAreasPayload,
    categoriesPayloads,
    categoryOrderPayloads,
  ] = payloads;

  const uniqueOrderPickingAreasPayload = uniquifyOrderByGroup('pickingAreaName', pickingAreasPayload);
  const combinedCategoriesPayloads = combineCategoryChanges(categoriesPayloads);
  const uniqueOrderCategoriesPayload = uniquifyOrderByGroup('categoryId', categoryOrderPayloads);
  const categoryOrderPayloadsByCategoryId = uniqueOrderCategoriesPayload.reduce<
    Record<string, { id: string; itemId: string; order: number }[]>
  >((acc, curr) => ({ ...acc, [curr.categoryId]: [...(acc[curr.categoryId] || []), curr] }), {});

  const itemPayloadChunks = splitEvery(UPDATE_ITEMS_BATCH_SIZE_LIMIT, itemUpdatePayloads);
  const pickingAreasPayloadChunks = splitEvery(
    new Set(uniqueOrderPickingAreasPayload.map(p => p.pickingAreaName)).size < PICKING_AREA_PAYLOAD_AREAS_LIMIT
      ? PICKING_AREA_PAYLOAD_ITEMS_LIMIT
      : PICKING_AREA_PAYLOAD_AREAS_LIMIT,
    uniqueOrderPickingAreasPayload,
  );

  const totalTasks =
    itemPayloadChunks.length +
    (itemInventoryPayloads.length ? 1 : 0) +
    (itemConfigsPayloads.length ? 1 : 0) +
    (itemCommissionPayloads.length ? 1 : 0) +
    pickingAreasPayloadChunks.length +
    combinedCategoriesPayloads.length +
    Object.keys(categoryOrderPayloadsByCategoryId).length;

  let completedTasks = 0;

  const errorData: { [itemId: string]: (string | undefined)[] } = {};

  for (const chunk of itemPayloadChunks) {
    completedTasks++;
    setSubmitPercentage((completedTasks / totalTasks) * 100);
    await bulkUpdateItems(menuId, chunk)
      .then(res =>
        Object.entries(res.failed).forEach(([id, error]) => (errorData[id] = concatErrors(errorData[id], error))),
      )
      .catch(error => chunk.forEach(({ id }) => (errorData[id] = concatErrors(errorData[id], getErrorContent(error)))));
  }

  if (itemInventoryPayloads.length > 0) {
    completedTasks++;
    setSubmitPercentage((completedTasks / totalTasks) * 100);
    await updateVenueInventories(venueId, itemInventoryPayloads).catch(error => {
      itemInventoryPayloads.forEach(
        ({ itemId }) => (errorData[itemId] = concatErrors(errorData[itemId], getErrorContent(error))),
      );
      return false;
    });
  }

  if (itemConfigsPayloads.length > 0) {
    completedTasks++;
    setSubmitPercentage((completedTasks / totalTasks) * 100);
    await bulkUpdateItemConfigurations(itemConfigsPayloads).catch(error => {
      itemConfigsPayloads.forEach(
        ({ itemId }) => (errorData[itemId] = concatErrors(errorData[itemId], getErrorContent(error))),
      );
      return false;
    });
  }

  if (itemCommissionPayloads.length > 0) {
    completedTasks++;
    setSubmitPercentage((completedTasks / totalTasks) * 100);
    await bulkUpdateItemPricing(merchantId, itemCommissionPayloads).catch(error => {
      itemCommissionPayloads.forEach(
        ({ item_id }) => (errorData[item_id] = concatErrors(errorData[item_id], getErrorContent(error))),
      );
      return false;
    });
  }

  for (const chunk of pickingAreasPayloadChunks) {
    completedTasks++;
    setSubmitPercentage((completedTasks / totalTasks) * 100);
    await bulkUpdatePickingAreas(venueId, chunk)
      .then(res => {
        res.data.forEach(itemRes => {
          if (itemRes.status.code !== 200) {
            errorData[itemRes.id] = concatErrors(
              errorData[itemRes.id],
              `${itemRes.status.message}: ${itemRes.status.details}.`,
            );
          }
        });
      })
      .catch(error => {
        chunk.forEach(({ itemId }) => (errorData[itemId] = concatErrors(errorData[itemId], getErrorContent(error))));
      });
  }

  for (const payload of combinedCategoriesPayloads) {
    completedTasks++;
    setSubmitPercentage((completedTasks / totalTasks) * 100);
    await bulkChangeCategory(menuId, payload).catch(error =>
      payload.item_ids.forEach(itemId => (errorData[itemId] = concatErrors(errorData[itemId], getErrorContent(error)))),
    );
  }

  for (const [categoryId, payload] of Object.entries(categoryOrderPayloadsByCategoryId)) {
    completedTasks++;
    setSubmitPercentage((completedTasks / totalTasks) * 100);
    await patchItemBindingsOrder(
      menuId,
      categoryId,
      payload.map(p => ({ id: p.id, order: p.order })),
    ).catch(error =>
      payload.forEach(({ itemId }) => (errorData[itemId] = concatErrors(errorData[itemId], getErrorContent(error)))),
    );
  }

  return errorData;
};

export default submitHandler;
